import { AdminRole } from "@repo/api-dtos"
import type { SupportedGames } from "@repo/game-configuration"
import {
	CalendarCheck,
	Languages,
	LineChart,
	ListTree,
	Play,
	RefreshCw,
	ScrollText,
	Timer,
} from "lucide-react"
import { toast } from "sonner"
import { gameHasSealedProductSupport } from "@/types/sealed-products.types"
import {
	confirmStep,
	type PaletteCommand,
	type PaletteContext,
	type PaletteStep,
} from "../types"
import { loadGames, type PaletteGame } from "./game-picker"

const CATALOGUE_WRITE = [AdminRole.CatalogueWrite, AdminRole.CatalogueDeploy]

const CRON_HANDLERS = [
	"process-order-lifecycle",
	"process-reminders",
	"process-kyc-reminders",
	"process-seller-inactivity",
	"process-seller-promotions",
	"process-risk-assessment",
	"process-stuck-fund-operations",
	"process-dispute-lifecycle",
	"process-expired-reviews",
	"process-referral-release",
	"refresh-vat-rates",
] as const

const isProductionAdmin = () =>
	typeof window !== "undefined" &&
	window.location.hostname === "admin.cardnexus.com"

async function lockedGameSlugs(ctx: PaletteContext): Promise<Set<string>> {
	try {
		const { locks } = await ctx.gameAdminClient.lock.getAllActiveLocks()
		return new Set(
			locks
				.filter((lock) => lock.locked && lock.gameSlug)
				.map((lock) => lock.gameSlug as string),
		)
	} catch (error) {
		console.error("[command-palette] game lock lookup failed", error)
		return new Set()
	}
}

export function buildGameTaskStep(
	_ctx: PaletteContext,
	game: PaletteGame,
	locked: boolean,
): PaletteStep {
	if (locked) {
		return {
			title: game.gameName,
			commands: [
				{
					id: `run:task:${game.gameSlug}:locked`,
					label: "Game is locked — view tasks",
					group: "run",
					icon: ListTree,
					perform: (c) => {
						c.navigate({
							to: "/tasks",
							search: { search: undefined, game: undefined, task: undefined },
						})
						c.close()
					},
				},
			],
		}
	}

	const commands: PaletteCommand[] = [
		{
			id: `run:task:${game.gameSlug}:import-game`,
			label: "Import game…",
			group: "run",
			icon: Play,
			perform: (c) => {
				c.navigate({
					to: "/tasks",
					search: {
						search: undefined,
						game: game.gameSlug,
						task: "import-game",
					},
				})
				c.close()
			},
		},
		...(gameHasSealedProductSupport(game.gameSlug as SupportedGames)
			? [
					{
						id: `run:task:${game.gameSlug}:import-sealed`,
						label: "Import sealed…",
						group: "run" as const,
						icon: Play,
						perform: (c: PaletteContext) => {
							c.navigate({
								to: "/tasks",
								search: {
									search: undefined,
									game: game.gameSlug,
									task: "import-sealed",
								},
							})
							c.close()
						},
					},
				]
			: []),
		{
			id: `run:task:${game.gameSlug}:scrape-languages`,
			label: "Scrape languages",
			group: "run",
			icon: Languages,
			perform: (c) => {
				c.push(
					confirmStep(`Scrape languages for ${game.gameName}`, async (cc) => {
						await cc.gameAdminClient.externalServices.triggerLanguageScrape({
							gameSlug: game.gameSlug as SupportedGames,
						})
						toast.success(`Language scrape queued for ${game.gameName}`)
					}),
				)
			},
		},
		{
			id: `run:task:${game.gameSlug}:regenerate-diff`,
			label: "Regenerate diff",
			group: "run",
			icon: RefreshCw,
			perform: (c) => {
				c.push(
					confirmStep(`Regenerate diff for ${game.gameName}`, async (cc) => {
						await cc.gameAdminClient.diff.generateDiff({
							gameSlug: game.gameSlug,
						})
						toast.success(`Diff regeneration queued for ${game.gameName}`)
					}),
				)
			},
		},
	]

	return { title: game.gameName, commands }
}

function gameActionCommand(options: {
	id: string
	label: string
	icon: PaletteCommand["icon"]
	confirmLabel: (game: PaletteGame) => string
	run: (ctx: PaletteContext, game: PaletteGame) => Promise<void>
}): PaletteCommand {
	return {
		id: options.id,
		label: options.label,
		icon: options.icon,
		group: "run",
		requiredRoles: CATALOGUE_WRITE,
		perform: async (ctx) => {
			const games = await loadGames(ctx)
			ctx.push({
				title: options.label,
				placeholder: "Pick a game…",
				commands: games.map((game) => ({
					id: `${options.id}:${game.gameSlug}`,
					label: game.gameName,
					group: "run" as const,
					keywords: [game.gameSlug],
					perform: (c) => {
						c.push(
							confirmStep(options.confirmLabel(game), (cc) =>
								options.run(cc, game),
							),
						)
					},
				})),
			})
		},
	}
}

export function buildRunCommands(): PaletteCommand[] {
	const commands: PaletteCommand[] = [
		{
			id: "run:task",
			label: "Run task on game…",
			description: "Import game or sealed, scrape languages, regenerate diff",
			icon: Play,
			group: "run",
			requiredRoles: CATALOGUE_WRITE,
			perform: async (ctx) => {
				const [games, locked] = await Promise.all([
					loadGames(ctx),
					lockedGameSlugs(ctx),
				])
				ctx.push({
					title: "Run task",
					placeholder: "Pick a game…",
					commands: games.map((game) => ({
						id: `run:task:${game.gameSlug}`,
						label: locked.has(game.gameSlug)
							? `${game.gameName} (locked)`
							: game.gameName,
						group: "run" as const,
						keywords: [game.gameSlug],
						perform: (c) => {
							c.push(buildGameTaskStep(c, game, locked.has(game.gameSlug)))
						},
					})),
				})
			},
		},
		gameActionCommand({
			id: "run:refresh-coverage",
			label: "Refresh catalogue coverage…",
			icon: RefreshCw,
			confirmLabel: (game) => `Refresh coverage for ${game.gameName}`,
			run: async (ctx, game) => {
				await ctx.gameAdminClient.catalogueQuality.refreshCoverage({
					gameSlug: game.gameSlug,
				})
				toast.success(`Coverage refreshed for ${game.gameName}`)
			},
		}),
		gameActionCommand({
			id: "run:refresh-price-stats",
			label: "Refresh price stats…",
			icon: LineChart,
			confirmLabel: (game) => `Refresh price stats for ${game.gameName}`,
			run: async (ctx, game) => {
				await ctx.gameAdminClient.catalogueQuality.refreshPriceStats({
					gameSlug: game.gameSlug,
				})
				toast.success(`Price stats refreshed for ${game.gameName}`)
			},
		}),
		gameActionCommand({
			id: "run:rebuild-review",
			label: "Rebuild catalogue review…",
			icon: RefreshCw,
			confirmLabel: (game) => `Rebuild catalogue review for ${game.gameName}`,
			run: async (ctx, game) => {
				await ctx.gameAdminClient.review.rebuild({ gameSlug: game.gameSlug })
				toast.success(`Catalogue review rebuilt for ${game.gameName}`)
			},
		}),
		{
			id: "run:monthly-close-cron",
			label: "Run monthly-close cron now",
			icon: CalendarCheck,
			group: "run",
			requiredRoles: [AdminRole.MarketplaceFinance],
			perform: (ctx) => {
				ctx.push(
					confirmStep("Run the monthly-close cron", async (c) => {
						await c.marketplaceClient.accounting.monthlyCloses.triggerCron({})
						toast.success("Monthly-close cron queued")
					}),
				)
			},
		},
		{
			id: "run:republish-manifest",
			label: "Republish scanner manifest",
			icon: ScrollText,
			group: "run",
			requiredRoles: [AdminRole.ScannerAdmin],
			perform: (ctx) => {
				ctx.push(
					confirmStep("Republish the scanner manifest", async (c) => {
						await c.marketplaceClient.cardScanner.republishManifest()
						toast.success("Manifest republished")
					}),
				)
			},
		},
	]

	if (!isProductionAdmin()) {
		commands.push({
			id: "run:trigger-cron",
			label: "Trigger cron handler…",
			description: "Fire a background job now (non-production)",
			icon: Timer,
			group: "run",
			requiredRoles: [AdminRole.Superadmin],
			perform: (ctx) => {
				ctx.push({
					title: "Trigger cron",
					placeholder: "Pick a handler…",
					commands: CRON_HANDLERS.map((handler) => ({
						id: `run:trigger-cron:${handler}`,
						label: handler,
						group: "run" as const,
						perform: (c) => {
							c.push(
								confirmStep(`Trigger ${handler}`, async (cc) => {
									await cc.marketplaceClient.devTools.triggerCron({ handler })
									toast.success(`${handler} triggered`)
								}),
							)
						},
					})),
				})
			},
		})
	}

	return commands
}
